import { type Contacts, EContactProvider } from '@albacross/types'

export type ShowFilter = Contacts.RequestCategory | 'all'

export const CATEGORY_LABELS: Record<Contacts.RequestCategory, string> = {
  charged: 'Charged',
  free: 'Free',
  refused: 'Refused',
  nothing_found: 'Nothing found',
  waiting: 'Waiting',
  failed: 'Failed or timed out',
  late: 'Late',
}

export const METER_LABELS: Record<Contacts.CreditMeter, string> = { email: 'Emails', phone: 'Phones' }

export const AUTO_ENGAGE_SOURCE_LABELS: Record<Contacts.AutoEngageSource, string> = {
  albacross_app: 'Albacross app',
  chrome_plugin: 'Chrome plugin',
  auto_engage: 'Auto-Engage',
}

export const originLabel = (origin: Contacts.RequestOrigin | null): string => {
  switch (origin?.kind) {
    case 'workflow':
      return `Workflow ${origin.workflowId}${origin.noFilter ? ' (retry without filters)' : ''}`
    case 'manual_send':
      return `Export to connector, workflow ${origin.workflowId}`
    case 'export':
      return `Export ${origin.exportId}, part ${origin.chunk + 1}`
    case 'reveal':
      return origin.userId ? `Reveal by user ${origin.userId}` : 'Reveal'
    case 'outreach_connection':
      return `Auto-Engage connection, campaign ${origin.campaignId}`
    case 'auto_engage':
      return AUTO_ENGAGE_SOURCE_LABELS[origin.source]
    case 'hashed':
      return 'Unknown (long reference, find it by trace id)'
    case 'other':
      return 'Other'
    default:
      return 'Before the ledger (no request recorded)'
  }
}

// Each provider reports cost in its own unit: Waterfall in USD, Cognism in credits (one per person bought).
export const costLabel = ({ provider, cost }: Pick<Contacts.ProviderJobSummary, 'provider' | 'cost'>) => {
  if (cost == null) return null
  if (provider === EContactProvider.Waterfall) return `$${cost.toFixed(2)}`
  if (provider === EContactProvider.Cognism) return `${cost} ${cost === 1 ? 'credit' : 'credits'}`
  return String(cost)
}

const FIND_IN_APP: Record<Contacts.CreditMeter, string> = {
  email: 'Find Email (Albacross app)',
  phone: 'Find Phone (Albacross app)',
}

// Without the export part or the retry: an item holds them all.
export const itemOriginLabel = (origin: Contacts.RequestOrigin) => {
  switch (origin.kind) {
    case 'workflow':
      return `Workflow ${origin.workflowId}`
    case 'export':
      return `Export ${origin.exportId}`
    default:
      return originLabel(origin)
  }
}

export const itemLabel = ({ origin, domains, requests }: Contacts.CreditRequestItem) => {
  if (origin.kind === 'auto_engage' && origin.source === 'albacross_app') return FIND_IN_APP[requests[0].meter]
  if (domains.length > 1) return `${itemOriginLabel(origin)}: ${domains.length} companies`
  return domains.length ? `${itemOriginLabel(origin)}, ${domains[0]}` : itemOriginLabel(origin)
}
